import * as THREE from 'three';
import { DDSLoader } from 'three/addons/loaders/DDSLoader.js';
import { parseGSC, decodeMesh, decodeFaceon } from './gsc.mjs';

// Shared original THINGS geometry for pickups and other named level objects.
// Templates share GPU resources; callers own placement and source runtime state.
export function createThings(buffer) {
  const parsed = parseGSC(buffer), templates = new Map(), textures = new Map(), resources = [], dds = new DDSLoader();
  const specials = new Map(parsed.specialObjects.map(s => [s.name.toLowerCase(), s]));
  function texture(index) {
    if (textures.has(index)) return textures.get(index);
    const source = parsed.textures.get(index); if (!source) return null;
    const image = dds.parse(source.data, true); if (!image.format || image.isCubemap) throw new Error('Unsupported original object texture.');
    const map = image.format === THREE.RGBAFormat
      ? new THREE.DataTexture(image.mipmaps[0].data, image.width, image.height, THREE.RGBAFormat)
      : new THREE.CompressedTexture(image.mipmaps, image.width, image.height, image.format);
    map.flipY = false; map.colorSpace = THREE.SRGBColorSpace;
    // Owned PC mesh UVs use negative V coordinates, including every stud quad
    // and the minikit atlas. Clamping samples an edge instead of the texture.
    map.wrapS = map.wrapT = THREE.RepeatWrapping;
    map.needsUpdate = true; textures.set(index, map); return map;
  }
  function create(name) {
    const key = name.toLowerCase();
    if (templates.has(key)) return templates.get(key).clone(true);
    const special = specials.get(key); if (!special?.parts.length) throw new Error(`Original object model not found: ${name}`);
    const group = new THREE.Group(); group.name = special.name;
    // The authored pan-coin models are camera-facing quads. BrickBench's
    // original pickup adapter likewise billboards these four stud types.
    const panCoin = /^(silver|gold|blue|purple)_pan_coin[1-4]$/.test(key);
    for (const part of special.parts) {
      const faceon = part.type === 'faceon', decoded = faceon ? decodeFaceon(parsed, part) : decodeMesh(parsed, part.mesh, part.material);
      if (faceon && decoded.orientation !== 0) throw new Error('Unsupported original object billboard orientation.');
      const geometry = new THREE.BufferGeometry(); resources.push(geometry);
      geometry.setAttribute('position', new THREE.BufferAttribute(decoded.positions, 3));
      geometry.setAttribute('uv', new THREE.BufferAttribute(decoded.uvs, 2));
      geometry.setAttribute('color', new THREE.BufferAttribute(decoded.colors, faceon ? 4 : 3));
      geometry.setIndex(new THREE.BufferAttribute(decoded.indices, 1));
      if (faceon) geometry.setAttribute('faceonOffset', new THREE.BufferAttribute(decoded.offsets, 2));
      else if (decoded.hasNormals) geometry.setAttribute('normal', new THREE.BufferAttribute(decoded.normals, 3));
      else geometry.computeVertexNormals();
      const source = part.material, alpha = source.alpha & 15, map = texture(source.texture);
      const material = new THREE.MeshBasicMaterial({ map, color: map ? 0xffffff : new THREE.Color(...source.color.slice(0, 3)),
        vertexColors: faceon || decoded.hasColors, transparent: faceon || (alpha >= 1 && alpha <= 3),
        blending: faceon ? THREE.AdditiveBlending : THREE.NormalBlending, depthWrite: !faceon, alphaTest: 0.015, side: THREE.DoubleSide });
      resources.push(material);
      if (faceon || panCoin) {
        material.onBeforeCompile = shader => {
          if (faceon) shader.vertexShader = 'attribute vec2 faceonOffset;\n' + shader.vertexShader;
          const scale = 'float faceonScale = sqrt((dot(modelMatrix[0].xyz, modelMatrix[0].xyz) + dot(modelMatrix[1].xyz, modelMatrix[1].xyz) + dot(modelMatrix[2].xyz, modelMatrix[2].xyz)) / 3.0);\n';
          const project = faceon
            ? 'vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);\nmvPosition.xy += faceonOffset * faceonScale;\n'
            : 'vec4 mvPosition = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);\nmvPosition.xyz += transformed * faceonScale;\n';
          shader.vertexShader = shader.vertexShader.replace('#include <project_vertex>', scale + project + 'gl_Position = projectionMatrix * mvPosition;');
        };
        material.customProgramCacheKey = () => faceon ? 'original-things-faceon-0-scaled' : 'original-things-pan-coin';
      }
      geometry.computeBoundingSphere();
      if (faceon) {
        let extent = 0; for (let i = 0; i < decoded.offsets.length; i += 2) extent = Math.max(extent, Math.hypot(decoded.offsets[i], decoded.offsets[i + 1]));
        geometry.boundingSphere.radius += extent;
      }
      const mesh = new THREE.Mesh(geometry, material); mesh.name = special.name; group.add(mesh);
    }
    templates.set(key, group); return group.clone(true);
  }
  return { create, has: name => specials.has(name.toLowerCase()), parsed,
    dispose() { resources.forEach(resource => resource.dispose()); textures.forEach(map => map.dispose()); templates.clear(); } };
}
